import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Select, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, Refresh } from '@mui/icons-material';

import {
  listRateLimitPolicies, createRateLimitPolicy, updateRateLimitPolicy, deleteRateLimitPolicy,
  listTenantRateLimits, getTenantRateLimit, setTenantTier,
  setTenantOverride, removeTenantOverride,
} from '../../actions/rateLimit';

const TIERS = ['premium', 'gold', 'silver', 'normal', 'unauthenticated'];

const blankPolicy = { name: '', capacity: '', fillRate: '', fillIntervalSecs: 1, notes: '' };
const blankOverride = { scope: 'ingress.sms', capacity: '', fillRate: '', fillIntervalSecs: 1, reason: '', expiresAt: '' };

const overrideToForm = (o) => ({
  scope: o.scope,
  capacity: o.capacity != null ? String(o.capacity) : '',
  fillRate: o.fillRate != null ? String(o.fillRate) : '',
  fillIntervalSecs: o.fillIntervalSecs != null ? o.fillIntervalSecs : 1,
  reason: o.reason || '',
  expiresAt: o.expiresAt || '',
});

const findOverrideForScope = (overrides, scope) =>
  (overrides || []).find(o => o.scope === scope) || null;

const detailOverridesFor = (detail, tenantId) => (
  (detail && detail.tenant && detail.tenant.tenantId === tenantId) ? (detail.overrides || []) : null
);

class RateLimits extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 0,
      // templates
      policyDialog: { open: false, mode: 'create', form: { ...blankPolicy } },
      // tenants
      tenantSearch: '',
      tenantPage: 1,
      tenantPageSize: 25,
      tierDialog: { open: false, tenantId: '', tenantName: '', tier: 'normal' },
      detailTenantId: null,
      overrideDialog: { open: false, tenantId: '', mode: 'create', awaitingDetail: false, form: { ...blankOverride } },
      snack: '',
    };
  }

  componentDidMount() {
    this.props.listRateLimitPolicies();
    this.props.listTenantRateLimits('', 1, 25);
  }

  componentDidUpdate(prevProps) {
    const { overrideDialog } = this.state;
    if (!overrideDialog.open || !overrideDialog.awaitingDetail) return;
    const detail = this.props.getTenantResponse?.data;
    const prevDetail = prevProps.getTenantResponse?.data;
    if (detail === prevDetail) return;
    const overrides = detailOverridesFor(detail, overrideDialog.tenantId);
    if (overrides == null) return;
    const existing = findOverrideForScope(overrides, overrideDialog.form.scope);
    this.setState(s => ({
      overrideDialog: {
        ...s.overrideDialog,
        awaitingDetail: false,
        ...(existing ? { mode: 'edit', form: overrideToForm(existing) } : {}),
      },
    }));
  }

  // ── Templates ────────────────────────────────────────────────────────

  openCreatePolicy = () => this.setState({
    policyDialog: { open: true, mode: 'create', form: { ...blankPolicy } },
  });

  openEditPolicy = (p) => this.setState({
    policyDialog: {
      open: true, mode: 'edit',
      form: { name: p.name, capacity: p.capacity, fillRate: p.fillRate, fillIntervalSecs: p.fillIntervalSecs, notes: p.notes || '' },
    },
  });

  closePolicyDialog = () => this.setState({ policyDialog: { open: false, mode: 'create', form: { ...blankPolicy } } });

  submitPolicy = async () => {
    const { mode, form } = this.state.policyDialog;
    const params = {
      capacity: parseInt(form.capacity, 10),
      fillRate: parseInt(form.fillRate, 10),
      fillIntervalSecs: parseInt(form.fillIntervalSecs, 10) || 1,
      notes: form.notes,
    };
    if (mode === 'create') {
      await this.props.createRateLimitPolicy({ ...params, name: form.name });
    } else {
      await this.props.updateRateLimitPolicy(form.name, params);
    }
    this.closePolicyDialog();
    this.props.listRateLimitPolicies();
  };

  deletePolicy = async (name) => {
    if (!window.confirm(`Delete policy ${name}? Tenants on this template will fall back to YAML or restart-time defaults.`)) return;
    await this.props.deleteRateLimitPolicy(name);
    this.props.listRateLimitPolicies();
  };

  // ── Per-tenant tier ──────────────────────────────────────────────────

  refreshTenants = () => this.props.listTenantRateLimits(this.state.tenantSearch, this.state.tenantPage, this.state.tenantPageSize);

  onTenantSearch = (e) => {
    const tenantSearch = e.target.value;
    this.setState({ tenantSearch, tenantPage: 1 }, () => {
      this.props.listTenantRateLimits(tenantSearch, 1, this.state.tenantPageSize);
    });
  };

  openTierDialog = (t) => this.setState({
    tierDialog: { open: true, tenantId: t.tenantId, tenantName: t.tenantName, tier: t.rateLimitTier || 'normal' },
  });

  closeTierDialog = () => this.setState({ tierDialog: { open: false, tenantId: '', tenantName: '', tier: 'normal' } });

  submitTier = async () => {
    const { tenantId, tier } = this.state.tierDialog;
    await this.props.setTenantTier(tenantId, { tier });
    this.closeTierDialog();
    this.refreshTenants();
  };

  expandTenant = (tenantId) => {
    if (this.state.detailTenantId === tenantId) {
      this.setState({ detailTenantId: null });
    } else {
      this.setState({ detailTenantId: tenantId });
      this.props.getTenantRateLimit(tenantId);
    }
  };

  // ── Per-tenant overrides ─────────────────────────────────────────────

  openOverrideDialog = (tenantId, presetOverride = null) => {
    if (presetOverride) {
      this.setState({
        overrideDialog: { open: true, tenantId, mode: 'edit', awaitingDetail: false, form: overrideToForm(presetOverride) },
      });
      return;
    }
    const overrides = detailOverridesFor(this.props.getTenantResponse?.data, tenantId);
    if (overrides == null) {
      this.setState({
        overrideDialog: { open: true, tenantId, mode: 'create', awaitingDetail: true, form: { ...blankOverride } },
      });
      this.props.getTenantRateLimit(tenantId);
      return;
    }
    const existing = findOverrideForScope(overrides, blankOverride.scope);
    this.setState({
      overrideDialog: {
        open: true,
        tenantId,
        mode: existing ? 'edit' : 'create',
        awaitingDetail: false,
        form: existing ? overrideToForm(existing) : { ...blankOverride },
      },
    });
  };

  closeOverrideDialog = () => this.setState({ overrideDialog: { open: false, tenantId: '', mode: 'create', awaitingDetail: false, form: { ...blankOverride } } });

  submitOverride = async () => {
    const { tenantId, form } = this.state.overrideDialog;
    await this.props.setTenantOverride(tenantId, {
      scope: form.scope,
      capacity: parseInt(form.capacity, 10),
      fillRate: parseInt(form.fillRate, 10),
      fillIntervalSecs: parseInt(form.fillIntervalSecs, 10) || 1,
      reason: form.reason || undefined,
      expiresAt: form.expiresAt || undefined,
    });
    this.closeOverrideDialog();
    if (this.state.detailTenantId === tenantId) {
      this.props.getTenantRateLimit(tenantId);
    }
    this.refreshTenants();
  };

  removeOverride = async (tenantId, scope) => {
    if (!window.confirm(`Remove override for ${tenantId} / ${scope}? Tenant will revert to the tier template.`)) return;
    await this.props.removeTenantOverride(tenantId, scope);
    if (this.state.detailTenantId === tenantId) {
      this.props.getTenantRateLimit(tenantId);
    }
    this.refreshTenants();
  };

  // ── Render ───────────────────────────────────────────────────────────

  renderTemplates() {
    const policies = (this.props.listPoliciesResponse?.data?.policies) || [];
    return (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Policy templates ({policies.length})</Typography>
          <Tooltip title="Refresh"><IconButton onClick={() => this.props.listRateLimitPolicies()}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={this.openCreatePolicy}>New policy</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Policy</TableCell>
                <TableCell align="right">
                  <Tooltip title="Maximum burst — most requests allowed at once per IP (bucket size)"><span>Max burst (req)</span></Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Requests added back to the bucket every refill interval — the sustained rate"><span>Refill (req)</span></Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="How often the refill happens, in seconds"><span>Refill every (s)</span></Tooltip>
                </TableCell>
                <TableCell>Last updated by</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map(p => (
                <TableRow key={p.name}>
                  <TableCell><code>{p.name}</code></TableCell>
                  <TableCell align="right">{p.capacity}</TableCell>
                  <TableCell align="right">{p.fillRate}</TableCell>
                  <TableCell align="right">{p.fillIntervalSecs}</TableCell>
                  <TableCell>{p.updatedBy || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.notes || ''}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => this.openEditPolicy(p)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => this.deletePolicy(p.name)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderTenants() {
    const tenants = (this.props.listTenantsResponse?.data?.tenants) || [];
    const total = this.props.listTenantsResponse?.data?.total || 0;
    const detail = this.props.getTenantResponse?.data;
    return (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Per-tenant rate limits ({total})</Typography>
          <TextField size="small" placeholder="Search by name or ID" value={this.state.tenantSearch} onChange={this.onTenantSearch} sx={{ width: 280 }} />
          <Tooltip title="Refresh"><IconButton onClick={this.refreshTenants}><Refresh /></IconButton></Tooltip>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Tenant ID</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Active overrides</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map(t => {
                const isExpanded = this.state.detailTenantId === t.tenantId;
                return (
                  <React.Fragment key={t.tenantId}>
                    <TableRow hover>
                      <TableCell>{t.tenantName}</TableCell>
                      <TableCell><code style={{ fontSize: 11 }}>{t.tenantId}</code></TableCell>
                      <TableCell><Chip label={t.rateLimitTier || 'normal'} size="small" /></TableCell>
                      <TableCell align="right">{t.activeOverrideCount || 0}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => this.expandTenant(t.tenantId)}>{isExpanded ? 'Collapse' : 'Detail'}</Button>
                        <Button size="small" onClick={() => this.openTierDialog(t)}>Set tier</Button>
                        <Button size="small" onClick={() => this.openOverrideDialog(t.tenantId)}>Add override</Button>
                      </TableCell>
                    </TableRow>
                    {isExpanded && detail && detail.tenant && detail.tenant.tenantId === t.tenantId && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ background: '#fafafa' }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Active overrides ({(detail.overrides || []).length})</Typography>
                          {(!detail.overrides || detail.overrides.length === 0) && <Typography variant="caption" color="textSecondary">No overrides — tier template applies.</Typography>}
                          {(detail.overrides || []).map(o => (
                            <Box key={o.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
                              <Chip label={o.scope} size="small" variant="outlined" />
                              <Typography variant="caption">burst {o.capacity}, refill {o.fillRate} every {o.fillIntervalSecs}s</Typography>
                              {o.reason && <Typography variant="caption" color="textSecondary">— {o.reason}</Typography>}
                              <Box sx={{ flex: 1 }} />
                              <IconButton size="small" onClick={() => this.openOverrideDialog(t.tenantId, o)}><Edit fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => this.removeOverride(t.tenantId, o.scope)}><Delete fontSize="small" /></IconButton>
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderPolicyDialog() {
    const { open, mode, form } = this.state.policyDialog;
    if (!open) return null;
    const set = (k, v) => this.setState(s => ({ policyDialog: { ...s.policyDialog, form: { ...s.policyDialog.form, [k]: v } } }));
    return (
      <Dialog open onClose={this.closePolicyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{mode === 'create' ? 'New policy template' : `Edit ${form.name}`}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Name (e.g. sms_premium)" value={form.name} onChange={(e) => set('name', e.target.value)} disabled={mode === 'edit'} helperText="Convention: {api_type}_{tier} or unauthenticated_global" />
          <TextField fullWidth margin="dense" type="number" label="Max burst (requests per IP)" value={form.capacity} onChange={(e) => set('capacity', e.target.value)} helperText="Bucket size — most requests one IP can send at once" />
          <TextField fullWidth margin="dense" type="number" label="Refill (requests per interval)" value={form.fillRate} onChange={(e) => set('fillRate', e.target.value)} helperText="Sustained rate = refill ÷ interval (e.g. 50 every 1s = 50 req/s)" />
          <TextField fullWidth margin="dense" type="number" label="Refill interval (seconds)" value={form.fillIntervalSecs} onChange={(e) => set('fillIntervalSecs', e.target.value)} />
          <TextField fullWidth margin="dense" multiline label="Notes (optional)" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closePolicyDialog}>Cancel</Button>
          <Button onClick={this.submitPolicy} variant="contained">{mode === 'create' ? 'Create' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderTierDialog() {
    const { open, tenantName, tier } = this.state.tierDialog;
    if (!open) return null;
    return (
      <Dialog open onClose={this.closeTierDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Set tier — {tenantName}</DialogTitle>
        <DialogContent>
          <Select fullWidth value={tier} onChange={(e) => this.setState(s => ({ tierDialog: { ...s.tierDialog, tier: e.target.value } }))}>
            {TIERS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Picks up within ~60s on every RLQS replica. No redeploy needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeTierDialog}>Cancel</Button>
          <Button onClick={this.submitTier} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderOverrideDialog() {
    const { open, mode, awaitingDetail, tenantId, form } = this.state.overrideDialog;
    if (!open) return null;
    const set = (k, v) => this.setState(s => ({ overrideDialog: { ...s.overrideDialog, form: { ...s.overrideDialog.form, [k]: v } } }));
    const onScopeChange = (v) => {
      const overrides = detailOverridesFor(this.props.getTenantResponse?.data, tenantId);
      const existing = overrides ? findOverrideForScope(overrides, v) : null;
      if (existing) {
        this.setState(s => ({
          overrideDialog: { ...s.overrideDialog, mode: 'edit', form: overrideToForm(existing) },
        }));
      } else {
        this.setState(s => ({
          overrideDialog: { ...s.overrideDialog, mode: 'create', form: { ...s.overrideDialog.form, scope: v } },
        }));
      }
    };
    const isEdit = mode === 'edit';
    return (
      <Dialog open onClose={this.closeOverrideDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? 'Update tenant override' : 'Add tenant override'}</DialogTitle>
        <DialogContent>
          {awaitingDetail && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
              Loading existing overrides…
            </Typography>
          )}
          {isEdit && !awaitingDetail && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
              Existing override loaded — saving will replace it.
            </Typography>
          )}
          <TextField fullWidth margin="dense" label="Scope" value={form.scope} onChange={(e) => onScopeChange(e.target.value)} helperText="e.g. ingress.sms / ingress.ui / egress.sms.<operator>" />
          <TextField fullWidth margin="dense" type="number" label="Max burst (requests per IP)" value={form.capacity} onChange={(e) => set('capacity', e.target.value)} helperText="Bucket size — most requests one IP can send at once" />
          <TextField fullWidth margin="dense" type="number" label="Refill (requests per interval)" value={form.fillRate} onChange={(e) => set('fillRate', e.target.value)} helperText="Sustained rate = refill ÷ interval" />
          <TextField fullWidth margin="dense" type="number" label="Refill interval (seconds)" value={form.fillIntervalSecs} onChange={(e) => set('fillIntervalSecs', e.target.value)} />
          <TextField fullWidth margin="dense" label="Expires at (RFC3339, optional)" value={form.expiresAt} onChange={(e) => set('expiresAt', e.target.value)} placeholder="2026-12-31T23:59:59Z" />
          <TextField fullWidth margin="dense" multiline label="Reason" value={form.reason} onChange={(e) => set('reason', e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeOverrideDialog}>Cancel</Button>
          <Button onClick={this.submitOverride} variant="contained">{isEdit ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { tab } = this.state;
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Rate-limit configuration</Typography>
        <Tabs value={tab} onChange={(_, v) => this.setState({ tab: v })} sx={{ mb: 2 }}>
          <Tab label="Templates" />
          <Tab label="Per-tenant" />
        </Tabs>
        {tab === 0 && this.renderTemplates()}
        {tab === 1 && this.renderTenants()}
        {this.renderPolicyDialog()}
        {this.renderTierDialog()}
        {this.renderOverrideDialog()}
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  listPoliciesResponse: state.rateLimit.listPoliciesResponse,
  listTenantsResponse: state.rateLimit.listTenantsResponse,
  getTenantResponse: state.rateLimit.getTenantResponse,
});

export default connect(mapStateToProps, {
  listRateLimitPolicies, createRateLimitPolicy, updateRateLimitPolicy, deleteRateLimitPolicy,
  listTenantRateLimits, getTenantRateLimit, setTenantTier,
  setTenantOverride, removeTenantOverride,
})(RateLimits);
